import { useState } from 'react';

export interface PlaybackIntent {
  autoplay: boolean;
  /** 1-based frame to seek to on load; undefined starts at frame 1. */
  startFrame: number | undefined;
}

interface UseFrozenPlaybackIntentArgs extends PlaybackIntent {
  isPinned: boolean;
}

/**
 * Keeps the playback intent a pane had when it was pinned, so global intent
 * changes don't re-run viewport setup on it. Freezing the global value rather
 * than the pane's current frame means pinning itself doesn't trigger a reload.
 */
export function useFrozenPlaybackIntent({
  isPinned,
  autoplay,
  startFrame,
}: UseFrozenPlaybackIntentArgs): PlaybackIntent {
  const [wasPinned, setWasPinned] = useState(isPinned);
  const [frozen, setFrozen] = useState<PlaybackIntent | null>(() =>
    isPinned ? { autoplay, startFrame } : null
  );

  // Set during render, not in an effect, so setup already sees the frozen
  // value on the render where the pin changes.
  if (isPinned !== wasPinned) {
    setWasPinned(isPinned);
    setFrozen(isPinned ? { autoplay, startFrame } : null);
  }

  if (isPinned && frozen) return frozen;
  return { autoplay, startFrame };
}
